// src/hooks/useAndroidPwaPrompt.ts
import { useEffect, useState } from "react";

/** Util: detect standalone (Android + iOS Safari) */
export const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (navigator as any).standalone === true;

/**
 * Global capture so we NEVER miss beforeinstallprompt (even if it fires before mount)
 */
declare global {
  interface Window {
    __deferredA2HS?: any;
    __a2hsInit?: boolean;
  }
}
if (typeof window !== "undefined" && !window.__a2hsInit) {
  window.__a2hsInit = true;
  window.__deferredA2HS = null;

  window.addEventListener("beforeinstallprompt", (e: any) => {
    console.log("[PWA] (global) beforeinstallprompt");
    e.preventDefault();
    window.__deferredA2HS = e;
    document.dispatchEvent(new Event("a2hs-ready"));
  });

  window.addEventListener("appinstalled", () => {
    console.log("[PWA] (global) appinstalled");
    window.__deferredA2HS = null;
    document.dispatchEvent(new Event("a2hs-cleared"));
  });
}

/**
 * Hook API:
 *  - isReady: show your "Install" button when true
 *  - promptInstall(): calls the native prompt
 *  - installed: hide install UI when already installed
 */
export const useAndroidPwaPrompt = () => {
  const [isReady, setIsReady] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const setFromGlobal = () => {
      if ((window as any).__deferredA2HS) setIsReady(true);
    };
    setFromGlobal();

    const onReady = () => {
      console.log("[PWA] (hook) a2hs-ready");
      setIsReady(true);
    };
    const onCleared = () => {
      console.log("[PWA] (hook) a2hs-cleared");
      setIsReady(false);
      setInstalled(isStandalone());
    };
    const onControllerChange = () => {
      const controlled = !!navigator.serviceWorker.controller;
      console.log("[PWA] controllerchange; controlled:", controlled);
    };
    const onVisibility = () => setInstalled(isStandalone());

    document.addEventListener("a2hs-ready", onReady);
    document.addEventListener("a2hs-cleared", onCleared);
    navigator.serviceWorker?.addEventListener?.("controllerchange", onControllerChange);
    window.addEventListener("visibilitychange", onVisibility);

    // ---- DEBUG INFO ----
    (async () => {
      console.log("[PWA] location.href:", window.location.href);
      console.log(
        "[PWA] display-mode:",
        window.matchMedia("(display-mode: standalone)").matches ? "standalone" : "browser"
      );
      try {
        const reg = await navigator.serviceWorker?.getRegistration();
        console.log("[PWA] SW registration scope:", reg?.scope ?? "(none)");
      } catch (e) {
        console.warn("[PWA] SW registration check failed:", e);
      }
      try {
        const r = await fetch(new URL("manifest.webmanifest", location.origin).toString(), { cache: "no-store" });
        console.log("[PWA] manifest fetch:", r.status, r.url);
      } catch (e) {
        console.warn("[PWA] manifest fetch failed:", e);
      }
    })();

    return () => {
      document.removeEventListener("a2hs-ready", onReady);
      document.removeEventListener("a2hs-cleared", onCleared);
      navigator.serviceWorker?.removeEventListener?.("controllerchange", onControllerChange);
      window.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const promptInstall = async () => {
    const evt = (window as any).__deferredA2HS;
    if (!evt) {
      console.warn("[PWA] promptInstall: no deferred prompt captured yet");
      return;
    }
    evt.prompt();
    const { outcome } = await evt.userChoice;
    console.log("[PWA] userChoice:", outcome); // "accepted" | "dismissed"
    (window as any).__deferredA2HS = null;
    setIsReady(false);
    setInstalled(isStandalone());
  };

  return { isReady, promptInstall, installed };
};
